// Einstellungen → Menü & Navigation
// Der Admin bestimmt team-weit Reihenfolge & Sichtbarkeit der Bereiche in der
// Seitenleiste (Drag & Drop + Ein-/Ausblenden) und kann zusätzlich pro
// Mitarbeiter festlegen, welche Bereiche nach dem Login sichtbar sind.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GripVertical, ChevronUp, ChevronDown, ChevronRight, Save, LayoutList,
  Eye, EyeOff, Users, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SIDEBAR_NAV_CONFIG, groupIdOf } from '../config/sidebarNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Baut aus der kanonischen Struktur die aktuell sortierte Team-Reihenfolge.
const buildTree = (config) => {
  const order = config.order || [];
  const groupOrder = config.group_order || {};
  const rank = (arr, key) => {
    const i = arr.indexOf(key);
    return i >= 0 ? i : 10000;
  };
  const top = SIDEBAR_NAV_CONFIG.map((b, idx) => ({ ...b, _idx: idx }));
  top.sort((a, b) => (rank(order, a.key) - rank(order, b.key)) || (a._idx - b._idx));
  return top.map((b) => {
    if (b.group && b.children) {
      const go = groupOrder[groupIdOf(b.key)] || [];
      const ch = b.children.map((c, i) => ({ ...c, _i: i }));
      ch.sort((a, c) => (rank(go, a.key) - rank(go, c.key)) || (a._i - c._i));
      return { ...b, children: ch };
    }
    return b;
  });
};

const MenuLayoutSettingsPage = () => {
  const [tree, setTree] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [teamHidden, setTeamHidden] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [staffList, setStaffList] = useState([]);
  const [target, setTarget] = useState('team'); // 'team' | <userId>
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(null); // { scope, index }

  const isTeam = target === 'team';

  useEffect(() => {
    (async () => {
      try {
        const [mng, stf] = await Promise.all([
          axios.get(`${API}/settings/sidebar-layout/manage`),
          axios.get(`${API}/settings/sidebar-layout/staff`),
        ]);
        setTree(buildTree(mng.data || {}));
        setHidden(new Set(mng.data?.hidden || []));
        setTeamHidden(new Set(mng.data?.hidden || []));
        setStaffList(stf.data?.staff || []);
      } catch {
        toast.error('Menü-Einstellungen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Beim Wechsel des Ziels (Team / Mitarbeiter) die passende hidden-Menge laden.
  useEffect(() => {
    if (isTeam) {
      setHidden(new Set(teamHidden));
      return;
    }
    const s = staffList.find((x) => x.id === target);
    setHidden(new Set(s?.hidden || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const toggleHidden = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const moveTop = (from, to) => {
    if (to < 0 || to >= tree.length) return;
    setTree((prev) => {
      const next = [...prev];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };

  const moveChild = (groupKey, from, to) => {
    setTree((prev) => prev.map((b) => {
      if (b.key !== groupKey || !b.children) return b;
      if (to < 0 || to >= b.children.length) return b;
      const ch = [...b.children];
      const [it] = ch.splice(from, 1);
      ch.splice(to, 0, it);
      return { ...b, children: ch };
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isTeam) {
        const order = tree.map((b) => b.key);
        const group_order = {};
        tree.forEach((b) => {
          if (b.group && b.children) group_order[groupIdOf(b.key)] = b.children.map((c) => c.key);
        });
        await axios.put(`${API}/settings/sidebar-layout`, {
          order, group_order, hidden: [...hidden],
        });
        setTeamHidden(new Set(hidden));
        toast.success('Team-Menü gespeichert');
      } else {
        await axios.put(`${API}/settings/sidebar-layout/staff/${target}`, {
          hidden: [...hidden],
        });
        // lokale Kopie aktualisieren
        setStaffList((prev) => prev.map((s) => (s.id === target ? { ...s, hidden: [...hidden] } : s)));
        toast.success('Mitarbeiter-Sichtbarkeit gespeichert');
      }
      // Live-Sidebar aktualisieren (betrifft den aktuell eingeloggten Admin)
      window.dispatchEvent(new Event('sidebar-layout-updated'));
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-400" data-testid="menu-layout-loading">Lädt …</div>;
  }

  const settingsHiddenWarning = hidden.has('/settings');

  const renderToggle = (key) => {
    const teamOff = !isTeam && teamHidden.has(key);
    return (
      <span className="flex items-center gap-1.5">
        {teamOff && <span className="text-[10px] text-gray-400 whitespace-nowrap">team-weit aus</span>}
        <Switch
          checked={!teamOff && !hidden.has(key)}
          disabled={teamOff}
          onCheckedChange={() => toggleHidden(key)}
          data-testid={`menu-toggle-${key}`}
        />
      </span>
    );
  };

  const renderArrows = (scope, idx, len, onMove) => (
    <div className="flex flex-col gap-0.5 flex-shrink-0">
      <button
        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
        disabled={idx === 0}
        onClick={() => onMove(idx, idx - 1)}
        data-testid={`menu-up-${scope}-${idx}`}
      ><ChevronUp size={14} /></button>
      <button
        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
        disabled={idx === len - 1}
        onClick={() => onMove(idx, idx + 1)}
        data-testid={`menu-down-${scope}-${idx}`}
      ><ChevronDown size={14} /></button>
    </div>
  );

  return (
    <div className="max-w-2xl" data-testid="menu-layout-settings">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutList className="h-5 w-5 text-teal-600" />
            Menü & Navigation
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Bestimme die Reihenfolge der Bereiche in der Seitenleiste und welche sichtbar sind.
            Die Reihenfolge gilt für das ganze Team. Für einzelne Mitarbeiter kannst du zusätzlich
            festlegen, was sie nach dem Login sehen dürfen.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Ziel-Auswahl: Team oder einzelner Mitarbeiter */}
          <div className="flex items-center gap-3">
            <Users size={16} className="text-gray-400" />
            <span className="text-sm text-gray-600">Einstellung für:</span>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="w-80" data-testid="menu-target-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team" data-testid="menu-target-team">Ganzes Team</SelectItem>
                {staffList.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`menu-target-${s.id}`}>
                    {s.name} · {s.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isTeam && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              Hier blendest du Bereiche nur für diesen Mitarbeiter aus. Die Reihenfolge wird team-weit
              festgelegt und ist hier nicht änderbar.
            </div>
          )}

          {settingsHiddenWarning && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="menu-settings-hidden-warning">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>„Einstellungen" ist ausgeblendet. Du erreichst diese Seite danach nur noch direkt über <code>/settings</code>.</span>
            </div>
          )}

          <ul className="rounded-lg border border-gray-100 overflow-hidden divide-y divide-gray-100">
            {tree.map((block, i) => {
              const isGroup = !!block.group;
              const isOpen = expanded.has(block.key);
              const blockHidden = hidden.has(block.key);
              return (
                <li key={block.key} className="bg-white" data-testid={`menu-row-${block.key}`}>
                  <div
                    draggable={isTeam}
                    onDragStart={() => isTeam && setDrag({ scope: 'top', index: i })}
                    onDragOver={(e) => isTeam && e.preventDefault()}
                    onDrop={() => { if (isTeam && drag?.scope === 'top') moveTop(drag.index, i); setDrag(null); }}
                    className={`flex items-center gap-3 px-3 py-2.5 ${drag?.scope === 'top' && drag.index === i ? 'opacity-50' : ''} ${blockHidden ? 'bg-gray-50' : ''}`}
                  >
                    {isTeam ? (
                      <GripVertical size={16} className="text-gray-300 cursor-grab flex-shrink-0" />
                    ) : <span className="w-4 flex-shrink-0" />}
                    {isTeam && renderArrows('top', i, tree.length, moveTop)}
                    {isGroup ? (
                      <button
                        className="flex items-center gap-1 text-left flex-1 min-w-0"
                        onClick={() => toggleExpand(block.key)}
                        data-testid={`menu-group-expand-${block.key}`}
                      >
                        {isOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                        <span className={`text-sm font-semibold truncate ${blockHidden ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{block.name}</span>
                        <span className="text-[11px] text-gray-400 ml-1">Ordner</span>
                      </button>
                    ) : (
                      <span className={`flex-1 text-sm font-medium truncate ${blockHidden ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{block.name}</span>
                    )}
                    <span className="flex items-center gap-1.5 flex-shrink-0 text-gray-300">
                      {blockHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                      {renderToggle(block.key)}
                    </span>
                  </div>

                  {isGroup && isOpen && (
                    <ul className="bg-gray-50 border-t border-gray-100 pl-8 pr-3 py-1 space-y-0.5">
                      {block.children.map((child, ci) => {
                        const childHidden = hidden.has(child.key);
                        return (
                          <li
                            key={child.key}
                            draggable={isTeam}
                            onDragStart={(e) => { if (isTeam) { e.stopPropagation(); setDrag({ scope: block.key, index: ci }); } }}
                            onDragOver={(e) => isTeam && e.preventDefault()}
                            onDrop={() => { if (isTeam && drag?.scope === block.key) moveChild(block.key, drag.index, ci); setDrag(null); }}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded ${drag?.scope === block.key && drag.index === ci ? 'opacity-50' : ''}`}
                            data-testid={`menu-child-row-${child.key}`}
                          >
                            {isTeam && <GripVertical size={14} className="text-gray-300 cursor-grab flex-shrink-0" />}
                            {isTeam && renderArrows(`child-${block.key}`, ci, block.children.length, (f, t) => moveChild(block.key, f, t))}
                            <span className={`flex-1 text-sm truncate ${childHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{child.name}</span>
                            {renderToggle(child.key)}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="menu-layout-save">
              <Save size={16} className="mr-2" />
              {saving ? 'Speichert …' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MenuLayoutSettingsPage;
